import { useCallback, useEffect, MouseEvent, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'

import { Button, NewPagination, Table } from 'components'
import { GridHeader } from '../GridHeader'

import { all } from 'store/TraceabilityTables/TraceabilityTables.selector'
import { all as allFoodSafety } from 'store/FoodSafety/FoodSafety.selector'
import { FoodSafetyActions } from 'store/FoodSafety'
import { all as allOrders } from 'store/Order/Order.selector'

import { INITIAL_STATE } from 'constants/traceability.constants'
import { getColumnsByTheme } from 'constants/foodSafety.constants'
import { useTranslation } from 'react-i18next'

import * as S from './styled'
import { ModalSendEmail } from 'components/ModalSendMail'
import CloudDownloadIcon from '@material-ui/icons/CloudDownload'
import { UserStorageService } from 'services'

export function FoodSafety() {
  const { t } = useTranslation()

  const module = UserStorageService.getViewModuleTheme()
  const baseColumns = getColumnsByTheme(module, UserStorageService.isAdmin())

  const dispatch = useDispatch()

  const columns = [...baseColumns]
  const [modalExport, setModalExport] = useState(false)
  const [filter, setFilter] = useState({ ...INITIAL_STATE })
  const { filters, tableLoading } = useSelector(all)
  const { ordersKeys } = useSelector(allOrders)
  const { foodSafety = [], filterFoodSafety } = useSelector(allFoodSafety)
  const { pagination } = foodSafety
  const [isNewPage, setNewPage] = useState({ ...pagination })
  const fetch = useCallback(() => {
    setFilter(filters)
    if (!foodSafety.items) {
      dispatch(FoodSafetyActions.getFoodSafety(filters, ordersKeys))
    }
  }, [dispatch, filters, ordersKeys, foodSafety.items])

  useEffect(() => {
    fetch()
  }, [fetch])

  async function handleChangePage(
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) {
    const limit = foodSafety.pagination.limit
    const newFilter = {
      ...filterFoodSafety,
      ...filters
    }
    newFilter.page = newPage
    newFilter.limit = limit
    dispatch(FoodSafetyActions.setFilters(newFilter))
    dispatch(FoodSafetyActions.getFoodSafety(newFilter, ordersKeys))
  }

  function handleChangeRowsPerPage(e) {
    const newFilter = {
      ...filterFoodSafety,
      ...filters
    }
    newFilter.limit = Number(e.target.value)
    dispatch(FoodSafetyActions.setFilters(newFilter))
    dispatch(FoodSafetyActions.getFoodSafety(newFilter, ordersKeys))
  }

  function handleInputPagination(name: string, value: number) {
    setNewPage({ ...pagination, [name]: value })
  }

  function handleNewPage() {
    const newPage = isNewPage.page
    const totalPages = pagination.totalPages
    const limit = foodSafety.pagination.limit

    const nextPage = Math.min(newPage, totalPages) // Garantir que a nova página não exceda o total de páginas

    const newFilter = {
      ...filterFoodSafety,
      ...filters,
      limit,
      page: nextPage
    }

    dispatch(FoodSafetyActions.setFilters(newFilter))
    dispatch(FoodSafetyActions.getFoodSafety(newFilter, ordersKeys))
    setNewPage({ ...pagination, page: nextPage })
  }

  function onExport() {
    setModalExport(true)
    dispatch(FoodSafetyActions.getFoodSafetyExported(filters, ordersKeys))
  }

  return (
    <S.FullGrid item xs={12}>
      <S.HeaderContainer>
        {foodSafety.items?.length > 0 && (
          <GridHeader title={t('common:foodSafetyChecklist')} />
        )}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <p
            style={{
              fontSize: '1.4rem',
              color: 'gray',
              marginRight: '1.5rem'
            }}
          >
            {foodSafety?.pagination?.totalItems} {t('common:register')}
          </p>
          <Button variant="primary" size="medium" onClick={onExport}>
            <S.ButtonContent>
              <CloudDownloadIcon />
              <S.ButtonText>{t('common:export')}</S.ButtonText>
            </S.ButtonContent>
          </Button>
        </div>
      </S.HeaderContainer>
      <S.GridTable>
        <Table
          columns={columns}
          rows={foodSafety.items || []}
          sortColumn={{ column: filter.column, sort: filter.direction }}
          isLoading={tableLoading}
          isScroll={foodSafety.items?.length > 10}
        />
        {modalExport && (
          <ModalSendEmail
            title={t('modalExportOrders:titleFoodSafety')}
            description={t('modalExportOrders:message')}
            onExit={() => setModalExport(false)}
          />
        )}
        {pagination?.totalItems > 0 && (
          <NewPagination
            count={pagination?.totalPages}
            onChangePage={handleChangePage}
            onChangeRowsPerPage={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            handleInput={handleInputPagination}
            handleNewPage={handleNewPage}
            page={pagination?.page}
            limit={pagination.limit}
          />
        )}
      </S.GridTable>
    </S.FullGrid>
  )
}
